import type { ReactNode } from 'react';
import { Link } from 'react-router';

export interface AuthLayoutProps {
    title: string;
    subtitle?: string;
    children: ReactNode;
    footer?: ReactNode;
}

/**
 * Split auth shell — form on the left, programme pitch on the right.
 * Mirrors the visual language of the podus.ai landing page: oversized italic
 * display type, orange accent, generous whitespace.
 */
export default function AuthLayout({ title, subtitle, children, footer }: AuthLayoutProps) {
    return (
        <div className="flex min-h-dvh flex-col lg:flex-row">
            {/* ── Form column ── */}
            <div className="flex flex-1 flex-col px-5 py-8 sm:px-10 lg:px-16 lg:py-12">
                <Link to="/" className="flex w-fit items-center gap-2">
                    <img src="/logo.svg" alt="Podus" className="h-8 w-auto" />
                    <span className="text-[15px] font-bold text-secondary">Ambassador</span>
                </Link>

                <div className="flex flex-1 items-center justify-center py-10">
                    <div className="w-full max-w-md">
                        <h1 className="text-[26px] font-bold leading-tight tracking-[-0.5px] text-secondary sm:text-[32px]">
                            {title}
                        </h1>
                        {subtitle && <p className="mt-3 text-[14px] leading-6 text-[#747474]">{subtitle}</p>}

                        <div className="mt-8">{children}</div>

                        {footer && <div className="mt-6 text-center text-[14px] text-[#747474]">{footer}</div>}
                    </div>
                </div>
            </div>

            {/* ── Pitch column — decorative, so it drops away on small screens ── */}
            <aside className="hidden bg-secondary px-16 py-16 lg:flex lg:w-[42%] lg:flex-col lg:justify-center">
                <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-primary">Podus Ambassador</p>
                <h2 className="mt-5 text-[44px] font-bold leading-[1.05] text-white">
                    Share justice.
                    <span className="mt-1 block font-normal italic text-[#AEAEAE]">Track your impact.</span>
                </h2>
                <p className="mt-6 max-w-sm text-[15px] leading-7 text-[#B5B5B5]">
                    Every person who signs up through your link is permanently attributed to you — with clicks, signups
                    and activations you can see in real time.
                </p>

                <dl className="mt-10 grid grid-cols-2 gap-6 border-t border-white/10 pt-8">
                    <div>
                        <dt className="text-[12px] text-[#8a8a8a]">Attribution window</dt>
                        <dd className="mt-1 text-[22px] font-bold text-white">30 days</dd>
                    </div>
                    <div>
                        <dt className="text-[12px] text-[#8a8a8a]">Your link</dt>
                        <dd className="mt-1 text-[22px] font-bold text-white">Yours forever</dd>
                    </div>
                </dl>
            </aside>
        </div>
    );
}
